import { ConnectSource, ConnectTarget } from '@carbon/icons-react';
import * as React from 'react';

import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { VoiceboxPanelLink } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelLink';
import { VoiceboxPanelLoadMore } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelLoadMore';
import {
  Relationship,
  RelationshipDirection,
} from 'bones/constants/voicebox/panel';
import { Accordion } from 'bones/design-system/components/Accordion';
import { TableBody } from 'bones/design-system/components/data-table/TableBody';
import { Icon } from 'bones/design-system/components/icons/Icon';
import { alphaSort } from 'bones/utils/sort/alphaSort';

export interface VoiceboxPanelRelationshipsProps {
  messageId: string;
  relationships: Relationship[];
}

export const VoiceboxPanelRelationships: React.VFC<
  VoiceboxPanelRelationshipsProps
> = ({ relationships, messageId }) => {
  const { copy, testIds } = useVoiceboxSchema();
  const { getLabelOrLocalName } = useVoiceboxPanelData();

  if (!relationships.length) {
    return null;
  }

  return (
    <Accordion
      badgeProps={{ children: relationships.length.toLocaleString() }}
      className="vb-voicebox-panel-relationships"
      icon="left"
      initialIsOpen
      label={copy.panel.relationship.accordion}
      size="md"
      testId={testIds.panel.relationships.accordion}
    >
      <div className="vb-data-table">
        <TableBody
          body={[...relationships]
            .sort(
              alphaSort(
                (relationship) =>
                  `${getLabelOrLocalName(relationship.predicateIri)}\u0000${
                    relationship.direction
                  }`
              )
            )
            .map((relationship, relationshipIdx) => (
              <React.Fragment key={relationship.predicateIri}>
                <td>
                  <div className="vb-voicebox-panel-table-icon-wrapper">
                    <Icon
                      IconComponent={
                        relationship.direction ===
                        RelationshipDirection.OUTGOING
                          ? ConnectSource
                          : ConnectTarget
                      }
                      size="sm"
                    />
                    {getLabelOrLocalName(relationship.predicateIri)}
                  </div>
                </td>
                <td>
                  <div>
                    {relationship.items.map((item, itemIdx) => (
                      <React.Fragment key={item}>
                        <VoiceboxPanelLink
                          iri={item}
                          messageId={messageId}
                          testId={testIds.panel.relationships.link(
                            item,
                            relationshipIdx,
                            itemIdx
                          )}
                        />
                      </React.Fragment>
                    ))}
                  </div>
                  <VoiceboxPanelLoadMore
                    data={relationship}
                    testId={testIds.panel.relationships.loadMore(
                      relationship.predicateIri,
                      relationshipIdx
                    )}
                  />
                </td>
              </React.Fragment>
            ))}
          header={
            <>
              <td style={{ width: '50%' }}>{copy.panel.relationship.label}</td>
              <td style={{ width: '50%' }}>{copy.panel.relationship.value}</td>
            </>
          }
          size="lg"
        />
      </div>
    </Accordion>
  );
};
